<template>
  <div class="ticket-validate">
    <!-- ------------------------------------------------------------------ -->
    <!-- Confirmação de um ingresso aberto pelo QR (deep-link)               -->
    <!-- ------------------------------------------------------------------ -->
    <vx-card v-if="isDeepLink" class="mb-base">
      <div v-if="previewLoading" class="tv-center">
        <i class="feather icon-loader tv-spin"></i>
        <p class="mt-2 mb-0">Carregando ingresso...</p>
      </div>

      <template v-else-if="preview">
        <h4 class="mb-1">Confirmar entrada</h4>
        <p class="tv-muted mb-3">
          Confira a pessoa e toque em confirmar. Abrir o link não registra entrada.
        </p>

        <ticket-result :result="preview" />

        <div class="tv-actions mt-3">
          <vs-button
            :disabled="!preview.can_check_in || confirming"
            @click="confirmDeepLink"
          >
            {{ confirming ? 'Confirmando...' : 'Confirmar entrada' }}
          </vs-button>
          <vs-button type="border" @click="goToScanner">Abrir o leitor</vs-button>
        </div>
      </template>

      <div v-else class="tv-center">
        <i class="feather icon-alert-circle tv-icon-danger"></i>
        <p class="mt-2 mb-0">{{ previewError || 'Ingresso não encontrado.' }}</p>
        <vs-button class="mt-3" type="border" @click="goToScanner">Abrir o leitor</vs-button>
      </div>
    </vx-card>

    <!-- ------------------------------------------------------------------ -->
    <!-- Leitor                                                              -->
    <!-- ------------------------------------------------------------------ -->
    <template v-else>
      <vx-card class="mb-base">
        <div class="tv-toolbar">
          <div class="tv-field">
            <label class="tv-label">Evento</label>
            <v-select
              v-model="selectedItem"
              :options="selectableItems"
              :clearable="false"
              :reduce="item => item"
              label="name"
              placeholder="Escolha o evento"
              :disabled="itemsLoading"
            >
              <template #option="item">
                {{ item.name }}
                <span v-if="item.occurrences_today" class="tv-badge-today">hoje</span>
              </template>
            </v-select>
            <small v-if="!itemsLoading && !items.length" class="tv-muted">
              Nenhum evento com ingresso está atribuído a você.
            </small>
            <small v-else-if="isAllItems" class="tv-muted">
              Digite o CPF e o sistema encontra a sessão. Para validar por código,
              escolha o evento.
            </small>
          </div>

          <div class="tv-modes">
            <button
              type="button"
              :class="['tv-mode', { 'tv-mode--on': mode === 'camera' }]"
              @click="setMode('camera')"
            >
              <i class="feather icon-camera"></i> Câmera
            </button>
            <button
              type="button"
              :class="['tv-mode', { 'tv-mode--on': mode === 'manual' }]"
              @click="setMode('manual')"
            >
              <i class="feather icon-edit-3"></i> Digitar
            </button>
          </div>
        </div>
      </vx-card>

      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/2 mb-base">
          <vx-card>
            <!-- Câmera -->
            <template v-if="mode === 'camera'">
              <ticket-scanner
                v-if="cameraAvailable"
                :paused="scannerPaused"
                @detected="onDetected"
                @unsupported="onCameraUnsupported"
                @denied="onCameraDenied"
              />

              <div v-else class="tv-camera-fallback">
                <i class="feather icon-camera-off tv-icon-muted"></i>
                <p class="mt-2 mb-1"><strong>{{ cameraMessage }}</strong></p>
                <p class="tv-muted mb-2">
                  O QR do ingresso é um link: a câmera nativa do celular lê e abre
                  esta tela já com o ingresso carregado. Ou use a aba "Digitar".
                </p>
                <vs-button size="small" type="border" @click="setMode('manual')">
                  Digitar código
                </vs-button>
              </div>
            </template>

            <!-- Digitação -->
            <template v-else>
              <form @submit.prevent="submitManual">
                <div class="tv-field mb-2">
                  <label class="tv-label">{{ manualLabel }}</label>
                  <vs-input
                    ref="manualInput"
                    v-model="manualValue"
                    class="w-full tv-manual-input"
                    :placeholder="manualPlaceholder"
                    autocomplete="off"
                    autocapitalize="characters"
                  />
                </div>
                <vs-button
                  class="w-full"
                  :disabled="!manualValue || !selectedItem || validating"
                  @click="submitManual"
                >
                  {{ validating ? 'Validando...' : 'Validar' }}
                </vs-button>
              </form>

              <p v-if="!selectedItem" class="tv-muted mt-2 mb-0">
                Escolha o evento antes de validar por código ou CPF.
              </p>
            </template>
          </vx-card>
        </div>

        <div class="vx-col w-full lg:w-1/2 mb-base">
          <vx-card>
            <!-- ----------------------------------------------------------- -->
            <!-- Desambiguação: a pessoa tem sessão em mais de um evento agora -->
            <!-- ----------------------------------------------------------- -->
            <template v-if="choice">
              <div class="tv-choice__head">
                <div class="tv-choice__name">{{ choice.holder.name }}</div>
                <div v-if="choice.holder.document_masked" class="tv-muted">
                  CPF {{ choice.holder.document_masked }}
                </div>
              </div>
              <p class="tv-choice__ask">{{ choice.message }}</p>

              <ul class="tv-choice__list">
                <li v-for="candidate in choice.candidates" :key="candidate.occurrence.scheduled_event_id">
                  <button
                    type="button"
                    class="tv-choice__option"
                    :class="{ 'tv-choice__option--used': candidate.already_checked_in }"
                    :disabled="validating"
                    @click="confirmChoice(candidate)"
                  >
                    <span class="tv-choice__hour">{{ candidate.occurrence.start_time }}</span>
                    <span class="tv-choice__body">
                      <span class="tv-choice__item">{{ candidate.item.name }}</span>
                      <!--
                        A data só aparece quando NÃO é hoje. No fluxo normal todo
                        candidato é de hoje e repetir a data em cada linha seria
                        ruído; mas com `force` (liberação manual de quem tem gestão)
                        o servidor pode devolver a sessão mais próxima de outro dia,
                        e aí a hora sozinha mentiria.
                      -->
                      <span v-if="!isToday(candidate.occurrence.date)" class="tv-choice__otherday">
                        {{ formatDate(candidate.occurrence.date) }}
                      </span>
                      <span v-if="candidate.already_checked_in" class="tv-choice__used">
                        já entrou às {{ (candidate.check_in.at || '').slice(11, 16) }}
                      </span>
                      <span v-else-if="candidate.occurrence.end_time" class="tv-muted">
                        até {{ candidate.occurrence.end_time }}
                      </span>
                    </span>
                    <i class="feather icon-chevron-right"></i>
                  </button>
                </li>
              </ul>

              <vs-button class="mt-2" size="small" type="border" @click="cancelChoice">
                Cancelar
              </vs-button>
            </template>

            <ticket-result v-else-if="result" :result="result" />
            <div v-else class="tv-center tv-muted">
              <i class="feather icon-maximize tv-icon-muted"></i>
              <p class="mt-2 mb-0">Aponte a câmera para o QR do ingresso.</p>
            </div>
          </vx-card>

          <vx-card v-if="recent.length" class="mt-base" title="Entradas desta sessão">
            <ul class="tv-recent">
              <li v-for="entry in recent" :key="entry.id" class="tv-recent__item">
                <div>
                  <div class="tv-recent__name">{{ entry.name }}</div>
                  <div class="tv-muted tv-recent__meta">{{ entry.at }} · {{ entry.channelLabel }}</div>
                </div>
                <button
                  v-if="!entry.undone"
                  type="button"
                  class="tv-undo"
                  :disabled="entry.undoing"
                  @click="undo(entry)"
                >
                  Desfazer
                </button>
                <span v-else class="tv-muted">desfeita</span>
              </li>
            </ul>
          </vx-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import TicketResult from '@/components/ticket/TicketResult.vue'
import TicketService from '@/services/api/TicketService'

/**
 * Validação de ingresso na portaria.
 *
 * Duas entradas para a mesma tela:
 *
 * 1. `/ingressos/validar` — o leitor. Câmera com validação automática (padrão das
 *    plataformas de evento: quem está na porta não toca na tela) ou digitação de
 *    código/CPF.
 * 2. `/ingressos/validar/:token` — aberta pelo QR. Aqui **não** valida sozinha:
 *    mostra quem é e espera a confirmação. Abrir um link por engano não pode
 *    queimar o ingresso de ninguém.
 *
 * O token vai no caminho da rota, e não na query, porque o guard do backoffice
 * guarda apenas `to.path` para restaurar o destino depois do login — uma query
 * seria perdida no caminho e o validador chegaria aqui de mãos vazias.
 *
 * **Por CPF o evento não é escolhido antes.** O seletor abre em "Todos os meus
 * eventos": digita-se o documento e o servidor procura a sessão. Se a pessoa tiver
 * mais de uma agora — duas turmas do mesmo clube no mesmo dia —, a resposta vem
 * `AMBIGUOUS` com a lista e quem escolhe é quem está olhando a pessoa. A ordem
 * antiga (escolher a turma primeiro) era adivinhação: errando, a entrada era
 * registrada na turma errada e aquela sessão virava `DO`, que consome cota de
 * plano — o aluno perdia a aula do mês sem ter entrado nela.
 */
export default {
  name: 'TicketValidate',
  components: {
    'v-select': vSelect,
    TicketScanner: () =>
      import(/* webpackChunkName: "ticket-scanner" */ '@/components/ticket/TicketScanner.vue'),
    TicketResult
  },
  data() {
    return {
      service: TicketService.build(this.$vs),
      items: [],
      itemsLoading: true,
      selectedItem: null,
      // Resposta AMBIGUOUS aguardando o toque do operador. Enquanto existe, ela
      // toma o lugar do veredito: são estados excludentes.
      choice: null,
      // O CPF que gerou a escolha. Guardado porque o campo é limpo entre as duas
      // chamadas e a segunda precisa da mesma credencial.
      pendingCredential: '',
      /**
       * Opção sintética do seletor. `reference_type`/`reference_id` ausentes é o
       * que sinaliza "não mande item" — ver submitManual().
       */
      allItemsOption: { all: true, name: 'Todos os meus eventos', ticket_type: 'CPF' },
      mode: 'camera',
      cameraAvailable: true,
      cameraMessage: '',
      result: null,
      recent: [],
      validating: false,
      scannerPaused: false,
      manualValue: '',
      preview: null,
      previewLoading: false,
      previewError: '',
      confirming: false
    }
  },
  computed: {
    isDeepLink() {
      return Boolean(this.$route.params.token)
    },
    /**
     * "Todos os meus eventos" na frente da lista.
     *
     * É a opção padrão porque é a pergunta certa na recepção: quem chega diz o
     * CPF, não a turma. Escolher um evento continua valendo — é o que libera a
     * digitação de código, que precisa do item para não ser ambígua.
     */
    selectableItems() {
      if (!this.items.length) return []

      return [this.allItemsOption, ...this.items]
    },
    isAllItems() {
      return Boolean(this.selectedItem && this.selectedItem.all)
    },
    /** O rótulo segue o tipo do evento escolhido — CPF e código não se misturam. */
    manualLabel() {
      return this.manualCredentialType === 'cpf' ? 'CPF do aluno' : 'Código do ingresso'
    },
    manualPlaceholder() {
      return this.manualCredentialType === 'cpf' ? '000.000.000-00' : 'ABCD-2345'
    },
    manualCredentialType() {
      if (this.isAllItems) return 'cpf'

      return this.selectedItem && this.selectedItem.ticket_type === 'CPF' ? 'cpf' : 'code'
    }
  },
  created() {
    if (this.isDeepLink) {
      this.loadPreview()
      return
    }

    this.loadItems()
  },
  methods: {
    // ---------------------------------------------------------------
    // Carga
    // ---------------------------------------------------------------
    async loadItems() {
      this.itemsLoading = true

      try {
        const data = await this.service.getValidatableItems()
        this.items = Array.isArray(data) ? data : []
        // Abre em "todos": na recepção a pessoa diz o CPF, não a turma. Escolher
        // um evento é o que passa a valer quando se digita código.
        if (this.items.length) this.selectedItem = this.allItemsOption
      } catch (error) {
        this.items = []
      } finally {
        this.itemsLoading = false
      }
    },

    async loadPreview() {
      this.previewLoading = true

      try {
        this.preview = await this.service.lookup(this.$route.params.token)
      } catch (error) {
        this.preview = null
        this.previewError = this.extractError(error)
      } finally {
        this.previewLoading = false
      }
    },

    // ---------------------------------------------------------------
    // Validação
    // ---------------------------------------------------------------
    onDetected(raw) {
      if (this.validating) return

      this.runValidation({
        credential_type: 'token',
        credential: this.extractToken(raw),
        channel: 'CAM'
      })
    },

    submitManual() {
      if (!this.manualValue || !this.selectedItem || this.validating) return

      const payload = {
        credential_type: this.manualCredentialType,
        credential: this.manualValue,
        channel: this.manualCredentialType === 'cpf' ? 'CPF' : 'MAN'
      }

      // Em "todos os meus eventos" o item fica de fora de propósito: é a ausência
      // dele que faz o servidor varrer a agenda do validador e, se preciso,
      // devolver a escolha.
      if (!this.isAllItems) {
        payload.reference_type = this.selectedItem.reference_type
        payload.reference_id = this.selectedItem.reference_id
      }

      this.runValidation(payload)
    },

    /**
     * O operador tocou numa das sessões da desambiguação.
     *
     * Reenvia com o item E a ocorrência. Só o item não bastaria: o servidor
     * reescolheria sozinho entre as sessões daquela turma, e o toque viraria
     * sugestão.
     */
    confirmChoice(candidate) {
      if (this.validating) return

      this.runValidation({
        credential_type: 'cpf',
        credential: this.pendingCredential,
        reference_type: candidate.item.reference_type,
        reference_id: candidate.item.reference_id,
        scheduled_event_id: candidate.occurrence.scheduled_event_id,
        channel: 'CPF'
      })
    },

    cancelChoice() {
      this.choice = null
      this.pendingCredential = ''
    },

    isToday(date) {
      return date === new Date().toISOString().slice(0, 10)
    },

    formatDate(date) {
      const parts = String(date || '').split('-')

      return parts.length === 3 ? `${parts[2]}/${parts[1]}` : date
    },

    async runValidation(payload) {
      this.validating = true
      this.scannerPaused = true

      try {
        const data = await this.service.validate(payload)

        // AMBIGUOUS não é veredito: nada foi registrado e a tela vira pergunta.
        // Sem o retorno tátil de recusa — não houve recusa, e um bip de erro na
        // porta manda a pessoa embora.
        if (data && data.result === 'AMBIGUOUS') {
          this.choice = data
          this.pendingCredential = payload.credential
          this.result = null

          return
        }

        this.choice = null
        this.pendingCredential = ''
        this.result = data
        this.feedback(Boolean(data && data.success))

        if (data && data.success && data.check_in) {
          this.recent.unshift({
            id: data.check_in.id,
            name: (data.holder && data.holder.name) || 'Entrada registrada',
            at: (data.check_in.at || '').slice(11, 16),
            channelLabel: data.check_in.channel_label || '',
            undone: false,
            undoing: false
          })
          this.recent = this.recent.slice(0, 20)
          this.manualValue = ''
        }
      } catch (error) {
        // Recusa de ingresso vem como 200 — cair aqui é erro de chamada mesmo
        // (sem permissão, corpo inválido, rede). O Rest já mostrou o toast.
        this.result = null
        this.choice = null
      } finally {
        this.validating = false
        // Uma pausa curta antes de voltar a ler: tempo de o operador enxergar o
        // veredito e de a pessoa sair da frente da câmera.
        setTimeout(() => {
          this.scannerPaused = false
        }, 1600)
      }
    },

    async confirmDeepLink() {
      this.confirming = true

      try {
        const data = await this.service.validate({
          credential_type: 'token',
          credential: this.$route.params.token,
          channel: 'CAM'
        })
        this.preview = { ...data, preview: false, can_check_in: false }
        this.feedback(Boolean(data && data.success))
      } catch (error) {
        // toast do Rest
      } finally {
        this.confirming = false
      }
    },

    async undo(entry) {
      entry.undoing = true

      try {
        await this.service.undoCheckIn(entry.id)
        entry.undone = true
      } catch (error) {
        // toast do Rest
      } finally {
        entry.undoing = false
      }
    },

    // ---------------------------------------------------------------
    // Apoio
    // ---------------------------------------------------------------
    /**
     * O QR carrega a URL inteira da tela de validação; o backend espera só o
     * token. Se o texto lido não for um link nosso, é devolvido como veio — pode
     * ser um token cru, e quem decide se vale é o servidor.
     */
    extractToken(raw) {
      const value = String(raw || '').trim()
      const marker = '/ingressos/validar/'
      const at = value.indexOf(marker)

      if (at === -1) return value

      return value.slice(at + marker.length).split(/[?#/]/)[0]
    },

    /**
     * Retorno tátil e sonoro. Numa quadra barulhenta o operador não olha a tela a
     * cada leitura — ele ouve e sente.
     */
    feedback(success) {
      if (navigator.vibrate) navigator.vibrate(success ? 60 : [50, 60, 50])

      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return

        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.frequency.value = success ? 880 : 240
        gain.gain.value = 0.05
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + (success ? 0.09 : 0.22))
        osc.onended = () => ctx.close()
      } catch (error) {
        // Áudio bloqueado antes de qualquer gesto do usuário. A vibração resolve.
      }
    },

    setMode(mode) {
      this.mode = mode

      if (mode === 'manual') {
        this.$nextTick(() => {
          const input = this.$refs.manualInput
          if (input && input.$el) {
            const field = input.$el.querySelector('input')
            if (field) field.focus()
          }
        })
      }
    },

    /**
     * Só chega aqui quando o navegador não expõe a câmera de jeito nenhum
     * (contexto inseguro, navegador antigo). A falta da `BarcodeDetector` nativa
     * — o caso de Safari e iOS — é resolvida pelo jsQR dentro do scanner, e não
     * chega a virar mensagem.
     */
    onCameraUnsupported() {
      this.cameraAvailable = false
      this.cameraMessage = 'Este navegador não dá acesso à câmera.'
    },

    onCameraDenied(reason) {
      this.cameraAvailable = false
      this.cameraMessage =
        reason === 'NotAllowedError'
          ? 'Permissão de câmera negada.'
          : 'Não foi possível abrir a câmera.'
    },

    goToScanner() {
      this.$router.push('/ingressos/validar').catch(() => {})
    },

    /** `NotificationException` chega com `messages` em JSON string. */
    extractError(error) {
      try {
        const raw = error && (error.data || error.response?.data)
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        const messages = parsed && parsed.messages
        const list = typeof messages === 'string' ? JSON.parse(messages) : messages

        return Array.isArray(list) ? list[0] : String(messages || '')
      } catch (e) {
        return ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.tv-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}

.tv-field {
  flex: 1 1 260px;
  min-width: 0;
}

.tv-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: #6e6b7b;
}

.tv-modes {
  display: flex;
  gap: 0.4rem;
}

.tv-mode {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.6rem 1rem;
  border: 1px solid #d8d6de;
  border-radius: 0.45rem;
  background: transparent;
  color: #6e6b7b;
  cursor: pointer;

  &--on {
    border-color: rgba(var(--vs-primary), 1);
    background: rgba(var(--vs-primary), 0.08);
    color: rgba(var(--vs-primary), 1);
  }
}

/* Alvo grande e fonte monoespaçada: quem digita está de pé, com pressa. */
.tv-manual-input ::v-deep input {
  height: 3rem;
  font-family: 'Courier New', Courier, monospace;
  font-size: 1.3rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tv-center {
  padding: 2rem 1rem;
  text-align: center;
}

.tv-muted {
  color: #6e6b7b;
  font-size: 0.85rem;
}

.tv-icon-muted {
  font-size: 2.2rem;
  color: #b9b9c3;
}

.tv-icon-danger {
  font-size: 2.2rem;
  color: #ea5455;
}

.tv-spin {
  display: inline-block;
  font-size: 2rem;
  animation: tv-spin 1s linear infinite;
}

@keyframes tv-spin {
  to {
    transform: rotate(360deg);
  }
}

.tv-camera-fallback {
  padding: 2rem 1rem;
  text-align: center;
}

.tv-badge-today {
  margin-left: 0.4rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.6rem;
  background: rgba(40, 199, 111, 0.14);
  color: #28c76f;
  font-size: 0.7rem;
}

.tv-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.tv-recent {
  margin: 0;
  padding: 0;
  list-style: none;
}

.tv-recent__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #ebe9f1;

  &:last-child {
    border-bottom: 0;
  }
}

.tv-recent__name {
  font-weight: 600;
}

.tv-recent__meta {
  font-size: 0.75rem;
}

.tv-undo {
  border: 0;
  background: transparent;
  color: rgba(var(--vs-primary), 1);
  font-size: 0.8rem;
  cursor: pointer;
}

/* ------------------------------------------------------------------ */
/* Escolha da sessão. Alvos grandes: quem toca está de pé, com a pessoa */
/* na frente e o celular numa mão.                                      */
/* ------------------------------------------------------------------ */
.tv-choice__head {
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #ebe9f1;
}

.tv-choice__name {
  font-size: 1.15rem;
  font-weight: 700;
}

.tv-choice__ask {
  margin: 0.7rem 0 0.5rem;
  color: #6e6b7b;
  font-size: 0.9rem;
}

.tv-choice__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.tv-choice__option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 3.4rem;
  margin-bottom: 0.5rem;
  padding: 0.6rem 0.8rem;
  border: 1px solid #d8d6de;
  border-radius: 0.5rem;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(var(--vs-primary), 1);
    background: rgba(var(--vs-primary), 0.06);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  /* Já usada continua clicável: o servidor responde ALREADY_CHECKED_IN com hora
     e validador, que é a informação que resolve a discussão na porta. */
  &--used {
    border-style: dashed;
  }
}

.tv-choice__hour {
  flex: 0 0 auto;
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.tv-choice__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.tv-choice__item {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tv-choice__used {
  color: #ff9f43;
  font-size: 0.75rem;
}

.tv-choice__otherday {
  color: #ea5455;
  font-size: 0.75rem;
  font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
  .tv-spin {
    animation: none;
  }
}
</style>
